import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import sellarModule from '../../../api/modules/seller.module';
import useDebounce from '../../../hooks/useDebounce';
import { getOrderStatusOptions } from '../../../utils/orderStatus';
import OrderDetailsDrawer from './OrderDetailsDrawer';
import { OrderTableView } from './OrderTable.view';

/**
 * The seller's orders, the container.
 *
 * The query is untouched: same `['seller-orders', {page, limit: 100, search,
 * status}]` key, same `buildParams` stripping, same `getSellerOrders` call,
 * same `keepPreviousData`, same fallback shape on failure. The `limit: 100`
 * against a rendered page size of 10 is preserved rather than "fixed", because
 * changing it changes what the backend is asked for.
 */
export default function OrderTable() {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [openDrawer, setOpenDrawer] = useState(false);

  const debouncedSearch = useDebounce(searchTerm, 500);

  const buildParams = (params) =>
    Object.fromEntries(
      Object.entries(params).filter(
        ([, value]) =>
          value !== '' &&
          value !== null &&
          value !== undefined &&
          !(typeof value === 'number' && isNaN(value)),
      ),
    );

  const queryKey = useMemo(
    () => [
      'seller-orders',
      {
        page: currentPage,
        limit: 100,
        search: debouncedSearch?.trim() || '',
        status: statusFilter || '',
      },
    ],
    [currentPage, debouncedSearch, statusFilter],
  );

  const { data: queryData, isFetching, isError, refetch } = useQuery({
    queryKey,
    queryFn: async () => {
      const params = buildParams(queryKey[1]);
      const { success, data: response, error } = await sellarModule.getSellerOrders(params);
      if (!success) {
        // eslint-disable-next-line no-console
        console.error('Error fetching orders:', error);
        return { orders: [], page: 1, page_size: pageSize, total_pages: 0 };
      }
      return response || { orders: [], page: 1, page_size: pageSize, total_pages: 0 };
    },
    keepPreviousData: true,
  });

  const data = queryData?.orders || [];

  return (
    <OrderTableView
      rows={data}
      loading={isFetching}
      error={isError ? 'We could not reach the orders service.' : null}
      onRetry={refetch}
      search={searchTerm}
      onSearchChange={setSearchTerm}
      status={statusFilter}
      onStatusChange={setStatusFilter}
      statusOptions={getOrderStatusOptions()}
      page={queryData?.page || 1}
      pageCount={queryData?.total_pages || 0}
      pageSize={queryData?.page_size || pageSize}
      total={queryData?.total ?? data.length}
      onPageChange={setCurrentPage}
      onView={(order) => {
        setSelectedOrder(order);
        setOpenDrawer(true);
      }}
      drawer={
        selectedOrder ? (
          <OrderDetailsDrawer
            isOpen={openDrawer}
            onClose={() => setOpenDrawer(false)}
            order={selectedOrder}
            seller
          />
        ) : null
      }
    />
  );
}
